import styled from "styled-components/macro";

import { SMALL_LAPTOP } from "src/utils/constants/media";
import { typography } from "src/utils/styledComponents/typography";
import { colors } from "src/theme/colors";
import { line, space } from "src/theme/ui";

/**
 * The balance panel beside the withdraw form.
 *
 * Three readings — total cash, available to withdraw, reserved — stacked as
 * label-over-figure in a periwinkle column with an accent heading and an
 * accent figure. Two faults, the same two the deposit panel had.
 *
 * It was ACCENT ON ACCENT, with no ink in the column at all: on this tenant
 * primary[10] is a brand tint rather than a neutral.
 *
 * And it printed a SUBTRACTION out of order. Total cash minus what is
 * reserved is what you can take out — that is the whole story of the panel —
 * and the three were listed total, answer, then the thing subtracted. A
 * reader who wanted to know why only thirty-two dollars of four and a half
 * thousand was available had to find the third row and do the sum. In order,
 * with the reserved amount signed and a rule above the total, the answer
 * explains itself.
 */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${typography.CustomText("1em")}
  padding: ${space.lg}px;
`;

export const Header = styled.h2`
  margin: 0 0 ${space.lg}px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
`;

export const Title = styled.span`
  font-size: 1em;
  font-weight: 700;
  color: #000;
`;

export const Definition = styled.p`
  margin-top: 1em;
`;

export const Body = styled.div``;

export const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 4em;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (max-width: ${SMALL_LAPTOP}px) {
    flex-direction: row;
    gap: ${space.xl}px;
  }
`;

/* Label left, figure right, so the figures make a column that can be read
   down as a sum. */
export const Item = styled.li<{ $total?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space.md}px;
  padding: 9px 0;

  ${({ $total }) =>
    $total
      ? `
        margin-top: 6px;
        padding-top: 14px;
        border-top: 1px solid ${line.hairline};
      `
      : ""}

  @media (max-width: ${SMALL_LAPTOP}px) {
    flex-direction: column;
    align-items: flex-start;
    border-top: 0;
    margin-top: 0;
    padding-top: 9px;
  }
`;

export const Text = styled.span`
  font-size: 12.5px;
  color: ${colors.gray[600]};
`;

export const NumberCell = styled.span<{ isBlue?: boolean; $total?: boolean }>`
  ${typography.CustomText("1em")}
  font-size: ${({ $total }) => ($total ? "20px" : "15px")};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${({ isBlue }) => (isBlue ? colors.primary[600] : colors.gray[900])};
`;
